import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, ListChecks, Link2, Quote, Undo2, Redo2, Check, Loader2, AlertCircle } from "lucide-react";

const isEmpty = (h) => !h || h === "<p></p>";

const Btn = ({ on, active, title, icon: Icon, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} title={title} aria-label={title}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-lg transition-colors ${active ? "bg-[#0A1128] text-white" : "text-[#0A1128] hover:bg-[#8FD0FB]"}`} data-testid={testid}>
    <Icon className="h-4 w-4" />
  </button>
);

const Toolbar = ({ editor, prefix }) => {
  const c = () => editor.chain().focus();
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Indirizzo del link (https://...)", prev);
    if (url === null) return;
    if (!url.trim()) return c().extendMarkRange("link").unsetLink().run();
    c().extendMarkRange("link").setLink({ href: /^(https?:|mailto:)/i.test(url) ? url.trim() : `https://${url.trim()}` }).run();
  };
  const items = [
    ["bold", "Grassetto", Bold, () => c().toggleBold().run(), editor.isActive("bold")],
    ["italic", "Corsivo", Italic, () => c().toggleItalic().run(), editor.isActive("italic")],
    ["h1", "Titolo", Heading1, () => c().toggleHeading({ level: 2 }).run(), editor.isActive("heading", { level: 2 })],
    ["h2", "Sottotitolo", Heading2, () => c().toggleHeading({ level: 3 }).run(), editor.isActive("heading", { level: 3 })],
    ["bullet", "Elenco puntato", List, () => c().toggleBulletList().run(), editor.isActive("bulletList")],
    ["ordered", "Elenco numerato", ListOrdered, () => c().toggleOrderedList().run(), editor.isActive("orderedList")],
    ["tasks", "Checklist", ListChecks, () => c().toggleTaskList().run(), editor.isActive("taskList")],
    ["quote", "Citazione", Quote, () => c().toggleBlockquote().run(), editor.isActive("blockquote")],
    ["link", "Link", Link2, link, editor.isActive("link")],
    ["undo", "Annulla", Undo2, () => c().undo().run(), false],
    ["redo", "Ripeti", Redo2, () => c().redo().run(), false],
  ];
  return (
    <div className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible items-center gap-0.5 border-b border-[#5FB3EA] px-2 py-1.5 sticky top-0 bg-[#A6DAFC] z-10 rounded-t-xl">
      {items.map(([k, t, I, on, a]) => <Btn key={k} on={on} active={a} title={t} icon={I} testid={`${prefix}-tb-${k}`} />)}
    </div>
  );
};

export const RichEditor = ({ value, onChange, placeholder = "Scrivi qui i tuoi appunti...", minHeight = 360, testid = "rich-editor" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer nofollow" } } }),
      TaskList, TaskItem.configure({ nested: true }), Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => { const h = e.getHTML(); onChange(isEmpty(h) ? "" : h); },
    editorProps: { attributes: { class: "core-rich focus:outline-none px-5 py-4", style: `min-height:${minHeight}px`, "data-testid": `${testid}-content` } },
  });
  if (!editor) return null;
  return (
    <div className="rounded-xl border border-[#5FB3EA] bg-[#EAF6FF]" data-testid={testid}>
      <Toolbar editor={editor} prefix={testid} />
      <EditorContent editor={editor} />
    </div>
  );
};

const STATUS = {
  dirty: [null, "Modifiche non salvate", "text-slate-600"], saving: [Loader2, "Salvataggio...", "text-slate-600"],
  saved: [Check, "Salvato", "text-emerald-700"], error: [AlertCircle, "Errore nel salvataggio", "text-[#C8102E]"],
  invalid: [AlertCircle, "Inserisci un titolo per salvare", "text-[#C8102E]"],
};

export const SaveIndicator = ({ status, testid = "save-indicator" }) => {
  const s = STATUS[status];
  if (!s) return <span className="text-xs text-slate-500" data-testid={testid}>Salvataggio automatico</span>;
  const [I, label, cls] = s;
  return <span className={`text-xs font-bold inline-flex items-center gap-1 ${cls}`} data-testid={testid}>{I && <I className={`h-3.5 w-3.5 ${status === "saving" ? "animate-spin" : ""}`} />}{label}</span>;
};
